import React, { useEffect, useState } from "react";
import { Spinner, Modal, Button } from "react-bootstrap";
import CommonLayout from "../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig } from "../../services/apiUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./TopSong.css";
import { artistImageUrl, extractObjectId } from "../../utils/utils";
import PageTargetSelector from "../common/PageTargetSelector";
import usePageTargetSelection from "../../hooks/usePageTargetSelection";
import {
  FaTrophy,
  FaMusic,
  FaSearch,
  FaUser,
  FaCompactDisc,
  FaPlay,
  FaEye,
  FaTrash,
  FaPlus,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";

const TopSong = () => {
  const navigate = useNavigate();
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;
  const [topSongs, setTopSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchResults, setSearchResults] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTopSongList = async (page = 1) => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getTopSong(
        { page, limit: 10, ...targetParams },
        getAuthConfig()
      );
      const { success, data } = response;

      if (success && Array.isArray(data.topSongsList)) {
        setTopSongs(data.topSongsList);
        setTotalCount(data.totalCount || data.topSongsList.length);
        setTotalPages(data.totalPages || 1);
      } else {
        console.warn("No data found or data is not an array");
      }
    } catch (error) {
      console.error("Error fetching top songs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchSong = async (song_name) => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getSearchSong(
        { song_name },
        getAuthConfig()
      );
      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
    } catch (error) {
      toast.error("Failed to fetch search song");
    } finally {
      setLoading(false);
    }
  };

  const addTopSong = async (song_id) => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.addTopSong(
        { song_id, ...targetParams },
        getAuthConfig()
      );
      if (response && response.success) {
        toast.success("Song added to top songs!");
        setSearchTerm("");
        setSearchResults([]);
        fetchTopSongList(currentPage);
      } else {
        toast.error("Failed to add song.");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOnDelete = async () => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.deleteTopSong(
        { id: deletingId },
        getAuthConfig()
      );

      if (response && response.status === 200) {
        setShowDeleteModal(false);
        toast.success("Song deleted successfully!");
        setTopSongs((prevSongs) =>
          prevSongs.filter((song) => song._id !== deletingId)
        );
      } else {
        toast.error("Failed to delete song!");
      }
    } catch (error) {
      toast.error("Failed to delete song!");
    } finally {
      setLoading(false);
      setDeletingId(null);
    }
  };

  const confirmDelete = (songId) => {
    setDeletingId(songId);
    setShowDeleteModal(true);
  };

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      fetchSearchSong(value);
    } else {
      setSearchResults([]);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  useEffect(() => {
    if (!searchTerm) {
      fetchTopSongList(currentPage);
    }
  }, [currentPage, searchTerm, targetParams.pageType, targetParams.genreSlug]);

  return (
    <CommonLayout>
      <div className="top-songs-page">
        {/* Header Section */}
        <div className="top-header">
          <div className="top-header-content">
            <div className="top-header-icon">
              <FaTrophy />
            </div>
            <div className="top-header-text">
              <h1 className="top-title">Top Songs</h1>
              <p className="top-subtitle">
                Curate the best songs for your platform
              </p>
            </div>
          </div>
          <div className="top-stats">
            <div className="top-stat-card">
              <FaMusic className="top-stat-icon" />
              <div className="top-stat-info">
                <span className="top-stat-value">{totalCount}</span>
                <span className="top-stat-label">Total Songs</span>
              </div>
            </div>
          </div>
        </div>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="Switch between Homepage and Genre targets before curating top songs."
        />

        {/* Search Controls */}
        <div className="top-controls">
          <div className="top-search-wrapper">
            <FaSearch className="top-search-icon" />
            <input
              type="text"
              className="top-search-input"
              placeholder="Search for a song to add to top songs..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="top-loading">
            <div className="top-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="top-loading-text">Loading songs...</p>
          </div>
        ) : searchTerm && searchResults.length > 0 ? (
          <>
            {/* Search Results Section */}
            <div className="top-section-header">
              <div className="top-section-icon">
                <FaSearch />
              </div>
              <h2 className="top-section-title">Search Results</h2>
              <span className="top-section-count">
                {searchResults.length} found
              </span>
            </div>
            <div className="top-grid">
              {searchResults.map((song, index) => (
                <div key={index} className="top-card top-card-search">
                  <div className="top-card-image-wrapper">
                    <img
                      src={artistImageUrl(song?.artist)}
                      alt={song.song_name}
                      className="top-card-image"
                    />
                    <div className="top-card-overlay">
                      <div className="top-card-play">
                        <FaPlay />
                      </div>
                    </div>
                    <div className="top-card-badge top-card-badge-search">
                      <FaSearch /> Result
                    </div>
                  </div>
                  <div className="top-card-content">
                    <h3 className="top-card-title">{song.song_name}</h3>
                    <div className="top-card-meta">
                      <div className="top-card-meta-item">
                        <FaUser className="top-card-meta-icon" />
                        <span className="top-card-meta-value">
                          {song?.artist?.artist_name || "Unknown Artist"}
                        </span>
                      </div>
                    </div>
                    <div className="top-card-actions">
                      <button
                        className="top-action-btn top-action-btn-add"
                        onClick={() => addTopSong(song.song_id)}
                      >
                        <FaPlus /> Add to Top Songs
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : topSongs.length === 0 ? (
          <div className="top-empty">
            <div className="top-empty-icon">
              <FaMusic />
            </div>
            <h3 className="top-empty-title">No Top Songs</h3>
            <p className="top-empty-text">
              Search for songs above to add them to your top songs list
            </p>
          </div>
        ) : (
          <>
            {/* Top Songs Grid */}
            <div className="top-grid">
              {topSongs
                .filter((song) =>
                  song.song_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                )
                .map((song, index) => (
                  <div key={index} className="top-card">
                    <div className="top-card-image-wrapper">
                      <img
                        src={artistImageUrl(song?.artist)}
                        alt={song.song_name}
                        className="top-card-image"
                      />
                      <div className="top-card-overlay">
                        <div className="top-card-play">
                          <FaPlay />
                        </div>
                      </div>
                      <div className="top-card-badge">
                        <FaTrophy /> Top
                      </div>
                    </div>
                    <div className="top-card-content">
                      <h3 className="top-card-title">{song.song_name}</h3>
                      <div className="top-card-meta">
                        <div className="top-card-meta-item">
                          <FaUser className="top-card-meta-icon" />
                          <span className="top-card-meta-value">
                            {song.artist?.artist_name || "Unknown Artist"}
                          </span>
                        </div>
                        <div className="top-card-meta-item">
                          <FaCompactDisc className="top-card-meta-icon" />
                          <span className="top-card-meta-value">
                            {song.album?.album_name || "No Album"}
                          </span>
                        </div>
                      </div>
                      <div className="top-card-actions">
                        <button
                          className="top-action-btn top-action-btn-view"
                          onClick={() => navigate(`/lyrics/${song.song_id}`)}
                        >
                          <FaEye /> View
                        </button>
                        <button
                          className="top-action-btn top-action-btn-delete"
                          onClick={() => confirmDelete(extractObjectId(song._id))}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="top-pagination">
                <button
                  className="top-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="top-pagination-info">
                  <span className="top-pagination-current">{currentPage}</span>
                  <span className="top-pagination-separator">/</span>
                  <span className="top-pagination-total">{totalPages}</span>
                </div>
                <button
                  className="top-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        className="top-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this song from the top songs list?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleOnDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </CommonLayout>
  );
};

export default TopSong;
